import classNames from 'classnames';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { useEffect, useState } from 'react';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import { SingleOption } from '@/components/blocks/pickup-point/pickup-point';
import { groupByPickupPoint } from '@/components/blocks/pickup-point/pickup-point-select-modal/pickup-point-select-modal.utils';
import PickupPointSelectMultiple from '@/components/blocks/pickup-point/pickup-point-select-multiple/pickup-point-select-multiple';
import PickupPointSelectSingle from '@/components/blocks/pickup-point/pickup-point-select-single/pickup-point-select-single';
import Button from '@/components/molecules/button/button';
import { SelectOption } from '@/components/molecules/select/select';
import Lightbox from '@/components/utilities/lightbox/lightbox';
import styles from './pickup-point-select-modal.module.scss';

type PaxGroup = {
  pickupPointId: string;
  uniquePaxHashes: string[];
};

export interface UpdateCollection {
  selected: PaxGroup[];
  unselected: {
    uniquePaxHashes: string[];
  };
}

export interface PickupPointSelectModalProps {
  /** The generic product choice type */
  choiceType?: Content.Properties.PickupPoint['choiceType'];
  /** The class name to apply */
  className?: string;
  /** The description of the modal */
  description?: string;
  /** The headline of the modal */
  headline?: string;
  /** Indicates whether the modal is open. */
  isOpen?: boolean;
  /** The array for multiple options to choose */
  multipleOptions?: SelectOption[];
  /** The callback to handle the save button click. */
  onClickSave?: (updateCollection: UpdateCollection) => void;
  /** The callback to handle the close event. */
  onClose: () => void;
  /** The passengers to display in the modal */
  passengers: Content.Components['PassengerDto'][];
  /** The single option to choose */
  singleOption?: SingleOption;
}

const PickupPointSelectModal = ({
  choiceType = 'single',
  className,
  description,
  headline,
  isOpen = false,
  multipleOptions = [],
  onClickSave,
  onClose,
  passengers = [],
  singleOption,
}: PickupPointSelectModalProps) => {
  const { t } = useI18n();

  const [updateCollection, setUpdateCollection] = useState<UpdateCollection>({
    selected: [],
    unselected: { uniquePaxHashes: [] },
  });

  const resetSelection = () => {
    const selected = groupByPickupPoint(passengers);

    setUpdateCollection({
      selected,
      unselected: { uniquePaxHashes: [] },
    });
  };

  const handleOnClose = () => {
    onClose();
    resetSelection();
  };

  const handleOnClickSave = () => {
    onClickSave?.(updateCollection);
    handleOnClose();
  };

  useEffect(() => {
    resetSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passengers]);

  const removePaxFromCollections = (
    previous: UpdateCollection,
    uniquePaxHash: string,
  ) => {
    let selected = [...previous.selected];

    selected = selected
      .map(item => ({
        ...item,
        uniquePaxHashes: item.uniquePaxHashes.filter(
          hash => hash !== uniquePaxHash,
        ),
      }))
      .filter(item => item.uniquePaxHashes.length > 0);

    const unselected = {
      uniquePaxHashes: previous.unselected.uniquePaxHashes.filter(
        hash => hash !== uniquePaxHash,
      ),
    };

    return { selected, unselected };
  };

  const addToSelected = (
    selected: PaxGroup[],
    pickupPointId: string,
    uniquePaxHash: string,
  ): PaxGroup[] => {
    const existing = selected.find(
      item => item.pickupPointId === pickupPointId,
    );

    return existing
      ? selected.map(item =>
          item.pickupPointId === pickupPointId
            ? {
                ...item,
                uniquePaxHashes: [...item.uniquePaxHashes, uniquePaxHash],
              }
            : item,
        )
      : [...selected, { pickupPointId, uniquePaxHashes: [uniquePaxHash] }];
  };

  const handleSingleSelect = (uniquePaxHash: string, pickupPointId: string) => {
    setUpdateCollection(previous => {
      const { selected: initialSelected, unselected } =
        removePaxFromCollections(previous, uniquePaxHash);

      let selected = initialSelected;

      const inSelected = previous.selected.some(item =>
        item.uniquePaxHashes.includes(uniquePaxHash),
      );

      if (inSelected) {
        return {
          selected,
          unselected: {
            uniquePaxHashes: [...unselected.uniquePaxHashes, uniquePaxHash],
          },
        };
      }

      selected = addToSelected(selected, pickupPointId, uniquePaxHash);

      return { selected, unselected };
    });
  };

  const handleMultipleSelect = (
    uniquePaxHash: string,
    pickupPointId: string,
  ) => {
    setUpdateCollection(previous => {
      const { selected: initialSelected, unselected } =
        removePaxFromCollections(previous, uniquePaxHash);

      let selected = initialSelected;

      if (pickupPointId === 'none') {
        return {
          selected,
          unselected: {
            uniquePaxHashes: [...unselected.uniquePaxHashes, uniquePaxHash],
          },
        };
      }

      selected = addToSelected(selected, pickupPointId, uniquePaxHash);

      return { selected, unselected };
    });
  };

  return (
    <Lightbox
      className={classNames(styles.component, className)}
      isCentered
      isFullWidthOnMobile
      isOpen={isOpen}
      onClose={handleOnClose}
      wrapperClassName={styles.lightboxWrapper}
    >
      <div className={classNames(styles.lightboxInnerWrapper)}>
        <div className={styles.contentWrapper}>
          <div className={styles.topSection}>
            {headline && (
              <Typography className={styles.headline} tagStyle="headingMedium">
                {headline}
              </Typography>
            )}

            {description && (
              <RichText className={styles.description} text={description} />
            )}

            {choiceType === 'single' && singleOption && (
              <div className={styles.singleOptionsContainer}>
                <Typography tagStyle="bodyMediumBold">
                  {t('items.modules.bookingFlow.select')}
                </Typography>

                <div className={styles.singleOptionsWrapper}>
                  {passengers.map((passenger, passengerIndex) => {
                    const { firstName, lastName, placeholders } = passenger;

                    const headline =
                      !firstName && !lastName
                        ? `${placeholders.firstName} ${placeholders.lastName}`
                        : `${firstName} ${lastName}`;

                    return (
                      <PickupPointSelectSingle
                        key={passengerIndex}
                        className={styles.singleOptionItem}
                        headline={headline}
                        isSelected={updateCollection.selected.some(item =>
                          item.uniquePaxHashes?.includes(
                            passenger.uniquePaxHash,
                          ),
                        )}
                        onClick={() => {
                          if (!singleOption) return;

                          handleSingleSelect(
                            passenger.uniquePaxHash,
                            singleOption.id.toString(),
                          );
                        }}
                        singleOption={singleOption}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {choiceType === 'multiple' && (
              <div className={styles.multipleOptionsWrapper}>
                {passengers.map((passenger, passengerIndex) => {
                  const { firstName, lastName, placeholders } = passenger;

                  const headline =
                    !firstName && !lastName
                      ? `${placeholders.firstName} ${placeholders.lastName}`
                      : `${firstName} ${lastName}`;

                  return (
                    <PickupPointSelectMultiple
                      key={passengerIndex}
                      headline={headline}
                      multipleOptions={multipleOptions}
                      onClick={pickupPointId =>
                        handleMultipleSelect(
                          passenger.uniquePaxHash,
                          pickupPointId,
                        )
                      }
                      selectedValue={
                        passenger.selectedPickupPoint?.pickupPointId?.toString() ||
                        'none'
                      }
                    />
                  );
                })}
              </div>
            )}
          </div>

          <div className={styles.bottomSection}>
            <Button
              className={styles.cta}
              onClick={handleOnClickSave}
              text={t('items.modules.bookingFlow.save')}
              variant="secondary"
            />
          </div>
        </div>
      </div>
    </Lightbox>
  );
};

PickupPointSelectModal.displayName = 'PickupPointSelectModal';

export default PickupPointSelectModal;
